import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast-service';
import { SystemDomainService, Domain, DomainWithModules, SaveDomainRequest, ModuleActivation, DomainNotificationConfig, SaveDomainNotificationConfigRequest } from '../../../services/system-domain.service';
import { SystemUserAccountService, UserAccount } from '../../../services/system-user-account.service';
import { GridColumn } from '../../../shared/smart-grid/smart-grid.models';
import { OAuthSettingsModalComponent } from './oauth-settings-modal/oauth-settings-modal.component';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-domain-management',
  templateUrl: './domain-management.component.html',
  styleUrls: ['./domain-management.component.scss']
})
export class DomainManagementComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Data
  domains: any = { rows: [], totalRows: 0 };
  userAccounts: UserAccount[] = [];
  
  // Form
  domainForm: FormGroup;
  currentDomain: DomainWithModules | null = null;
  isEditing = false;
  
  // Notification Config
  notificationConfigForm: FormGroup;
  currentNotificationConfig: DomainNotificationConfig | null = null;
  selectedDomainForNotification: Domain | null = null;
  
  // Modules
  availableModules: ModuleActivation[] = [];
  
  // Modals
  private modalRef?: NgbModalRef;
  @ViewChild('domainModal') domainModal!: TemplateRef<any>;
  @ViewChild('moduleModal') moduleModal!: TemplateRef<any>;
  @ViewChild('notificationConfigModal') notificationConfigModal!: TemplateRef<any>;
  @ViewChild('deleteConfirmModal') deleteConfirmModal!: TemplateRef<any>;
  @ViewChild('apiKeyModal') apiKeyModal!: TemplateRef<any>;
  @ViewChild('regenerateApiKeyConfirmModal') regenerateApiKeyConfirmModal!: TemplateRef<any>;
  
  // Delete
  domainToDelete: Domain | null = null;
  
  // API Key
  newApiKey: string = '';
  
  // API Key Regeneration
  domainToRegenerate: Domain | null = null;
  confirmRegenerateCheckbox: boolean = false;
  private regenerateModalRef?: NgbModalRef;
  
  // Grid columns
  columns: GridColumn<any>[] = [];

  constructor(
    private systemDomainService: SystemDomainService,
    private systemUserAccountService: SystemUserAccountService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translateService: TranslateService,
    private modalService: NgbModal
  ) {
    this.domainForm = this.formBuilder.group({
      domainID: [0],
      domainName: ['', [Validators.required, Validators.maxLength(100)]],
      databaseName: ['', [Validators.required, Validators.maxLength(30)]],
      databaseUsername: ['', [Validators.required, Validators.maxLength(30)]],
      databasePassword: ['', [Validators.required, Validators.maxLength(250)]],
      userAccountID: [1, Validators.required],
      domainColors: [''],
      userLogo: [''],
      userBackground: [''],
      userLogoSmall: [''],
      useExternalWorkOrders: [false],
      useStockIntegration: [false],
      useBugReport: [false],
      useGlobalSearch: [false],
      isMonitoringEnabled: [false],
      erpClientNumber: [''],
      domainUsers: [100, [Validators.required, Validators.min(0)]],
      domainGroups: [100, [Validators.required, Validators.min(0)]]
    });

    this.notificationConfigForm = this.formBuilder.group({
      isActive: [false],
      checkIntervalMinutes: [5, [Validators.required, Validators.min(1), Validators.max(1440)]],
      enableNotifications: [false],
      enableTimeScheduler: [false],
      enableIntegrations: [false],
      enableCrmTaskReminders: [false],
      enableCrmAssignmentReminders: [false],
      enableCrmAgreementExpiry: [false],
      enableChecklistReminders: [false],
      enableDeviationReminders: [false],
      enableProjectTaskReminders: [false],
      enableGoalReminders: [false],
      enableRiskReviewReminders: [false],
      enableBankReconciliation: [false]
    });
  }

  ngOnInit() {
    this.buildColumns();
    this.loadDomains();
    this.loadUserAccounts();
  }

  get f() {
    return this.domainForm.controls;
  }

  get ncf() {
    return this.notificationConfigForm.controls;
  }

  /**
   * Build grid columns
   */
  buildColumns() {
    this.columns = [
      {
        field: 'domainID',
        title: this.translateService.instant('SystemAdmin.domainID'),
        width: 80,
        sortable: true,
        filterable: true,
        filterType: 'number'
      },
      {
        field: 'domainName',
        title: this.translateService.instant('SystemAdmin.domainName'),
        width: 200,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'databaseName',
        title: this.translateService.instant('SystemAdmin.databaseName'),
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text'
      },
      {
        field: 'domainUsers',
        title: this.translateService.instant('SystemAdmin.users'),
        width: 80,
        sortable: true,
        filterable: true,
        filterType: 'number'
      },
      {
        field: 'domainGroups',
        title: this.translateService.instant('SystemAdmin.groups'),
        width: 80,
        sortable: true,
        filterable: true,
        filterType: 'number'
      },
      {
        field: 'domainAPIKey',
        title: this.translateService.instant('SystemAdmin.apiKey'),
        width: 150,
        sortable: false,
        format: (value: string) => value ? '••••••••••' : ''
      },
      {
        field: 'actions',
        title: this.translateService.instant('General.actions'),
        width: 250,
        sortable: false,
        filterable: false
      }
    ];
  }

  /**
   * Load all domains
   */
  loadDomains() {
    this.systemDomainService.getAllDomains().pipe(takeUntil(this.destroy$)).subscribe({
      next: (domains) => {
        this.domains = {
          rows: domains,
          totalRows: domains.length
        };
      },
      error: (error) => {
        console.error('Error loading domains:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingDomains')]
        });
      }
    });
  }

  /**
   * Load all user accounts for dropdown
   */
  loadUserAccounts() {
    this.systemUserAccountService.getAllUserAccounts().pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.userAccounts = response.result;
        }
      },
      error: (error) => {
        console.error('Error loading user accounts:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingUserAccounts')]
        });
      }
    });
  }

  /**
   * Open modal to create new domain
   */
  openCreateModal() {
    this.isEditing = false;
    this.currentDomain = null;
    this.domainForm.reset({
      domainID: 0,
      userAccountID: 1,
      domainColors: '#007bff,#6c757d,#28a745,#dc3545,#ffc107'
    });
    this.modalRef = this.modalService.open(this.domainModal, { size: 'lg', backdrop: 'static' });
  }

  /**
   * Open modal to edit domain
   */
  openEditModal(domain: Domain) {
    this.isEditing = true;
    
    // Load domain with modules
    this.systemDomainService.getDomainWithModules(domain.domainID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (domainWithModules) => {
        this.currentDomain = domainWithModules;
        this.domainForm.patchValue({
          domainID: domainWithModules.domainID,
          domainName: domainWithModules.domainName,
          databaseName: domainWithModules.databaseName,
          databaseUsername: domainWithModules.databaseUsername,
          databasePassword: domainWithModules.databasePassword,
          userAccountID: domainWithModules.userAccountID,
          domainColors: domainWithModules.domainColors,
          userLogo: domainWithModules.userLogo,
          userBackground: domainWithModules.userBackground,
          userLogoSmall: domainWithModules.userLogoSmall,
          useExternalWorkOrders: !!domainWithModules.useExternalWorkOrders,
          useStockIntegration: !!domainWithModules.useStockIntegration,
          useBugReport: !!domainWithModules.useBugReport,
          useGlobalSearch: !!domainWithModules.useGlobalSearch,
          isMonitoringEnabled: !!domainWithModules.isMonitoringEnabled,
          erpClientNumber: domainWithModules.erpClientNumber || '',
          domainUsers: domainWithModules.domainUsers ?? 100,
          domainGroups: domainWithModules.domainGroups ?? 100
        });
        this.modalRef = this.modalService.open(this.domainModal, { size: 'lg', backdrop: 'static' });
      },
      error: (error) => {
        console.error('Error loading domain:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingDomain')]
        });
      }
    });
  }

  /**
   * Save domain (create or update)
   */
  saveDomain() {
    if (this.domainForm.invalid) {
      Object.keys(this.domainForm.controls).forEach(key => {
        this.domainForm.controls[key].markAsTouched();
      });
      return;
    }

    const formValue = this.domainForm.value;
    const request: SaveDomainRequest = {
      domainID: this.isEditing ? formValue.domainID : undefined,
      domainName: formValue.domainName,
      databaseName: formValue.databaseName,
      databaseUsername: formValue.databaseUsername,
      databasePassword: formValue.databasePassword,
      userAccountID: formValue.userAccountID,
      domainColors: formValue.domainColors,
      userLogo: formValue.userLogo,
      userBackground: formValue.userBackground,
      userLogoSmall: formValue.userLogoSmall,
      useExternalWorkOrders: formValue.useExternalWorkOrders || false,
      useStockIntegration: formValue.useStockIntegration || false,
      useBugReport: formValue.useBugReport || false,
      useGlobalSearch: formValue.useGlobalSearch || false,
      isMonitoringEnabled: formValue.isMonitoringEnabled || false,
      erpClientNumber: formValue.erpClientNumber || '',
      domainUsers: formValue.domainUsers ?? 100,
      domainGroups: formValue.domainGroups ?? 100,
      activeModuleIDs: []
    };

    if (this.isEditing) {
      this.systemDomainService.updateDomain(formValue.domainID, request).pipe(takeUntil(this.destroy$)).subscribe({
        next: (result) => {
          if (result) {
            this.toastService.show({
              type: 'success',
              data: [this.translateService.instant('SystemAdmin.domainUpdated')]
            });
            this.loadDomains();
            this.closeModal();
          }
        },
        error: (error) => {
          console.error('Error updating domain:', error?.message ?? error?.status ?? 'unknown');
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('SystemAdmin.errorUpdatingDomain')]
          });
        }
      });
    } else {
      this.systemDomainService.createDomain(request).pipe(takeUntil(this.destroy$)).subscribe({
        next: (domainId) => {
          if (domainId) {
            this.toastService.show({
              type: 'success',
              data: [this.translateService.instant('SystemAdmin.domainCreated')]
            });
            this.loadDomains();
            this.closeModal();
          }
        },
        error: (error) => {
          console.error('Error creating domain:', error?.message ?? error?.status ?? 'unknown');
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('SystemAdmin.errorCreatingDomain')]
          });
        }
      });
    }
  }

  /**
   * Open module configuration modal
   */
  openModuleModal(domain: Domain) {
    this.systemDomainService.getDomainWithModules(domain.domainID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (domainWithModules) => {
        this.currentDomain = domainWithModules;
        this.availableModules = domainWithModules.modules;
        this.modalRef = this.modalService.open(this.moduleModal, { size: 'lg', backdrop: 'static' });
      },
      error: (error) => {
        console.error('Error loading modules:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingModules')]
        });
      }
    });
  }

  /**
   * Save module configuration
   */
  saveModules() {
    if (!this.currentDomain) return;

    const activeModuleIds = this.availableModules
      .filter(m => m.isActive)
      .map(m => m.moduleID);

    this.systemDomainService.updateDomainModules(this.currentDomain.domainID, activeModuleIds).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.modulesUpdated')]
          });
          this.closeModal();
        }
      },
      error: (error) => {
        console.error('Error updating modules:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorUpdatingModules')]
        });
      }
    });
  }

  /**
   * Toggle module activation
   */
  toggleModule(module: ModuleActivation) {
    module.isActive = !module.isActive;
  }

  /**
   * Open delete confirmation modal
   */
  openDeleteModal(domain: Domain) {
    this.domainToDelete = domain;
    this.modalRef = this.modalService.open(this.deleteConfirmModal, { backdrop: 'static' });
  }

  /**
   * Delete domain
   */
  confirmDelete() {
    if (!this.domainToDelete) return;

    this.systemDomainService.deleteDomain(this.domainToDelete.domainID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.domainDeleted')]
          });
          this.loadDomains();
          this.closeModal();
        }
      },
      error: (error) => {
        console.error('Error deleting domain:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorDeletingDomain')]
        });
      }
    });
  }

  /**
   * Open regenerate API key confirmation modal
   */
  openRegenerateApiKeyModal(domain: Domain) {
    this.domainToRegenerate = domain;
    this.confirmRegenerateCheckbox = false;
    this.regenerateModalRef = this.modalService.open(this.regenerateApiKeyConfirmModal, { 
      backdrop: 'static',
      centered: true
    });
  }

  /**
   * Close regenerate API key modal
   */
  closeRegenerateModal() {
    this.regenerateModalRef?.close();
    this.domainToRegenerate = null;
    this.confirmRegenerateCheckbox = false;
  }

  /**
   * Confirm and execute API key regeneration
   */
  confirmRegenerateApiKey() {
    if (!this.domainToRegenerate || !this.confirmRegenerateCheckbox) {
      return;
    }

    this.systemDomainService.regenerateAPIKey(this.domainToRegenerate.domainID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (newKey) => {
        this.newApiKey = newKey;
        this.closeRegenerateModal();
        this.modalRef = this.modalService.open(this.apiKeyModal, { backdrop: 'static' });
        this.loadDomains();
      },
      error: (error) => {
        console.error('Error regenerating API key:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorRegeneratingApiKey')]
        });
      }
    });
  }

  /**
   * Copy API key to clipboard
   */
  copyApiKey() {
    navigator.clipboard.writeText(this.newApiKey).then(() => {
      this.toastService.show({
        type: 'success',
        data: [this.translateService.instant('SystemAdmin.apiKeyCopied')]
      });
    });
  }

  /**
   * Copy existing API key to clipboard
   */
  copyExistingApiKey(domain: Domain) {
    if (domain.domainAPIKey) {
      navigator.clipboard.writeText(domain.domainAPIKey).then(() => {
        this.toastService.show({
          type: 'success',
          data: [this.translateService.instant('SystemAdmin.apiKeyCopied')]
        });
      }).catch(() => {
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorCopyingApiKey')]
        });
      });
    }
  }

  /**
   * Open notification config modal
   */
  openNotificationConfigModal(domain: Domain) {
    this.selectedDomainForNotification = domain;
    
    // Load existing config
    this.systemDomainService.getDomainNotificationConfig(domain.domainName).pipe(takeUntil(this.destroy$)).subscribe({
      next: (config) => {
        this.currentNotificationConfig = config;
        if (config) {
          this.notificationConfigForm.patchValue({
            isActive: config.isActive,
            checkIntervalMinutes: config.checkIntervalMinutes,
            enableNotifications: config.enableNotifications ?? false,
            enableTimeScheduler: config.enableTimeScheduler ?? false,
            enableIntegrations: config.enableIntegrations ?? false,
            enableCrmTaskReminders: config.enableCrmTaskReminders ?? false,
            enableCrmAssignmentReminders: config.enableCrmAssignmentReminders ?? false,
            enableCrmAgreementExpiry: config.enableCrmAgreementExpiry ?? false,
            enableChecklistReminders: config.enableChecklistReminders ?? false,
            enableDeviationReminders: config.enableDeviationReminders ?? false,
            enableProjectTaskReminders: config.enableProjectTaskReminders ?? false,
            enableGoalReminders: config.enableGoalReminders ?? false,
            enableRiskReviewReminders: config.enableRiskReviewReminders ?? false,
            enableBankReconciliation: config.enableBankReconciliation ?? false
          });
        } else {
          // Default values for new config — all toggles OFF
          this.notificationConfigForm.patchValue({
            isActive: false,
            checkIntervalMinutes: 5,
            enableNotifications: false,
            enableTimeScheduler: false,
            enableIntegrations: false,
            enableCrmTaskReminders: false,
            enableCrmAssignmentReminders: false,
            enableCrmAgreementExpiry: false,
            enableChecklistReminders: false,
            enableDeviationReminders: false,
            enableProjectTaskReminders: false,
            enableGoalReminders: false,
            enableRiskReviewReminders: false,
            enableBankReconciliation: false
          });
        }
        this.modalRef = this.modalService.open(this.notificationConfigModal, { size: 'lg', backdrop: 'static' });
      },
      error: (error) => {
        console.error('Error loading notification config:', error?.message ?? error?.status ?? 'unknown');
        // Set default values even on error — all toggles OFF
        this.notificationConfigForm.patchValue({
          isActive: false,
          checkIntervalMinutes: 5,
          enableNotifications: false,
          enableTimeScheduler: false,
          enableIntegrations: false,
          enableCrmTaskReminders: false,
          enableCrmAssignmentReminders: false,
          enableCrmAgreementExpiry: false,
          enableChecklistReminders: false,
          enableDeviationReminders: false,
          enableProjectTaskReminders: false,
          enableGoalReminders: false,
          enableRiskReviewReminders: false,
          enableBankReconciliation: false
        });
        this.modalRef = this.modalService.open(this.notificationConfigModal, { size: 'lg', backdrop: 'static' });
      }
    });
  }

  /**
   * Open OAuth settings modal
   */
  openOAuthSettings(domain: Domain) {
    const modalRef = this.modalService.open(OAuthSettingsModalComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.domainId = domain.domainID;
    modalRef.componentInstance.domainName = domain.domainName;
    modalRef.componentInstance.databaseName = domain.databaseName;
    
    // Refresh grid if changes saved
    modalRef.result.then(
      (result) => {
        if (result === 'saved') {
          this.toastService.show({ 
            type: 'success', 
            data: [this.translateService.instant('OAuth.saveConfigSuccess')] 
          });
          this.loadDomains();
        }
      },
      () => {} // Dismissed
    );
  }

  /**
   * Save notification config
   */
  saveNotificationConfig() {
    if (this.notificationConfigForm.invalid || !this.selectedDomainForNotification) {
      Object.keys(this.notificationConfigForm.controls).forEach(key => {
        this.notificationConfigForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.notificationConfigForm.value;
    const request: SaveDomainNotificationConfigRequest = {
      domainName: this.selectedDomainForNotification.domainName,
      isActive: formValue.isActive,
      checkIntervalMinutes: formValue.checkIntervalMinutes,
      enableNotifications: formValue.enableNotifications,
      enableTimeScheduler: formValue.enableTimeScheduler,
      enableIntegrations: formValue.enableIntegrations,
      enableCrmTaskReminders: formValue.enableCrmTaskReminders,
      enableCrmAssignmentReminders: formValue.enableCrmAssignmentReminders,
      enableCrmAgreementExpiry: formValue.enableCrmAgreementExpiry,
      enableChecklistReminders: formValue.enableChecklistReminders,
      enableDeviationReminders: formValue.enableDeviationReminders,
      enableGoalReminders: formValue.enableGoalReminders,
      enableRiskReviewReminders: formValue.enableRiskReviewReminders,
      enableProjectTaskReminders: formValue.enableProjectTaskReminders,
      enableBankReconciliation: formValue.enableBankReconciliation
    };

    this.systemDomainService.saveDomainNotificationConfig(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: (success) => {
        if (success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.scheduledJobsConfigSaved')]
          });
          this.closeModal();
        } else {
          this.toastService.show({
            type: 'error',
            data: [this.translateService.instant('General.operationFailed')]
          });
        }
      },
      error: (error) => {
        console.error('Error saving notification config:', error?.message ?? error?.status ?? 'unknown');
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('General.operationFailed')]
        });
      }
    });
  }

  /**
   * Close modal
   */
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = undefined;
    }
    this.domainToDelete = null;
    this.currentDomain = null;
    this.currentNotificationConfig = null;
    this.selectedDomainForNotification = null;
    this.newApiKey = '';
  }
}
